import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  palette: {
    primary: { main: '#85D1DB', dark: '#6BB8C3', light: '#B3EBF2', contrastText: '#ffffff' },
    secondary: { main: '#B6F2D1', dark: '#A4E0BF', light: '#C9FDF2', contrastText: '#ffffff' },
    error: { main: '#ef4444' },
    warning: { main: '#f59e0b' },
    success: { main: '#10b981' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
  },
  typography: {
    fontFamily: "'Roboto', sans-serif",
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
    h4: { fontWeight: 600, letterSpacing: '-0.02em' },
    h5: { fontWeight: 600, letterSpacing: '-0.015em' },
    h6: { fontWeight: 600, letterSpacing: '-0.01em' },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.57 },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 4 },
  components: {

    // ─── Button ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontFamily: "'Roboto', sans-serif",
          fontWeight: 600,
          fontSize: '0.875rem',
          borderRadius: 6,
          padding: '8px 20px',
          lineHeight: 1.5,
          transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:active': { transform: 'scale(0.98)' },
        },
        sizeSmall: { padding: '6px 14px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '10px 24px', fontSize: '0.9375rem' },
        containedPrimary: {
          background: 'linear-gradient(to right, #60c2c0, #4eb1af)',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(96, 194, 192, 0.3)',
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.9)',
            background: 'linear-gradient(to right, #60c2c0, #4eb1af)',
            opacity: 0.65,
            boxShadow: 'none',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          borderColor: '#e2e8f0',
          color: '#475569',
          borderRadius: 6,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          '&:hover': { borderWidth: '1.5px', borderColor: '#cbd5e1', backgroundColor: '#f8fafc' },
        },
      },
    },

    // ─── Icon Button ─────────────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 6, transition: 'all 0.2s ease' },
        sizeSmall: { padding: 6 },
      },
    },

    // ─── Card ─────────────────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid rgba(226,232,240,0.8)',
          boxShadow: '0 1px 4px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.02)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '&:last-child': { paddingBottom: '24px' },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { padding: '16px 24px', borderBottom: '1px solid #f1f5f9' },
        title: { fontSize: '1rem', fontWeight: 600, color: '#0f172a', letterSpacing: '-0.005em' },
        subheader: { fontSize: '0.8125rem', color: '#64748b', marginTop: 2 },
      },
    },

    // ─── Paper ───────────────────────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: { fontFamily: "'Roboto', sans-serif" },
        elevation1: { boxShadow: '0 1px 4px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)' },
        rounded: { borderRadius: 8 },
      },
    },

    // ─── TextField ───────────────────────────────────────────────────────────
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            fontFamily: "'Roboto', sans-serif",
            fontSize: '0.875rem',
            borderRadius: 6,
            backgroundColor: '#ffffff',
          },
          '& .MuiInputLabel-root': {
            fontFamily: "'Roboto', sans-serif",
            fontSize: '0.875rem',
            color: '#64748b',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            maxWidth: 'none',
          },
          '& .MuiInputLabel-root.Mui-focused': { color: '#14b8a6' },
          '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            backgroundColor: '#fff',
            padding: '0 4px',
            whiteSpace: 'nowrap',
            overflow: 'visible',
            maxWidth: 'none',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.875rem',
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#85D1DB',
            borderWidth: '1.5px',
          },
        },
        input: { padding: '8px 14px' },
        inputSizeSmall: { padding: '6px 12px' },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: { fontFamily: "'Roboto', sans-serif", fontSize: '0.875rem' },
        input: { '&::placeholder': { color: '#94a3b8', opacity: 1 } },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: { fontFamily: "'Roboto', sans-serif", fontSize: '0.875rem', color: '#64748b' },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: '0.75rem', marginLeft: 2 },
      },
    },

    // ─── Select ──────────────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontSize: '0.875rem',
          '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(133,209,219,0.15)' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#85D1DB',
            borderWidth: '1.5px',
          },
        },
      },
    },

    // ─── Avatar ──────────────────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 36,
          height: 36,
          fontSize: '0.8125rem',
          fontWeight: 700,
          fontFamily: "'Roboto', sans-serif",
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: { width: 30, height: 30, fontSize: '0.75rem', fontWeight: 700 },
      },
    },

    // ─── List ─────────────────────────────────────────────────────────────────
    MuiListItem: {
      styleOverrides: {
        root: { paddingTop: 8, paddingBottom: 8 },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: { fontSize: '0.875rem', fontWeight: 500, color: '#0f172a' },
        secondary: { fontSize: '0.8125rem', color: '#64748b', marginTop: 2 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          fontSize: '0.875rem',
          padding: '8px 16px',
          borderRadius: 4,
          '&.Mui-selected': { backgroundColor: 'rgba(133,209,219,0.12)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(133,209,219,0.2)' },
        },
      },
    },

    // ─── Divider ─────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#f1f5f9' },
      },
    },

    // ─── Dialog ──────────────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        root: { backdropFilter: 'blur(8px)', backgroundColor: 'rgba(15, 23, 42, 0.4)' },
        paper: {
          borderRadius: 12,
          boxShadow: '0 32px 64px rgba(15,23,42,0.15), 0 8px 16px rgba(15,23,42,0.1)',
          backgroundImage: 'linear-gradient(to bottom, #ffffff, #f8fafc)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 28px 14px',
          '& .MuiTypography-root': {
            fontWeight: 700,
            fontSize: '1.125rem',
            letterSpacing: '-0.01em',
            color: '#0f172a',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '16px 28px 24px' },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 28px 20px', gap: '10px' },
      },
    },

    // ─── Table ───────────────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            backgroundColor: '#f8fafc',
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: '#64748b',
            whiteSpace: 'nowrap',
            padding: '12px 20px',
            borderBottom: '1px solid #e2e8f0',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          fontSize: '0.875rem',
          color: '#334155',
          borderBottom: '1px solid #f1f5f9',
          padding: '12px 20px',
          transition: 'background-color 0.15s',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: '#f8fafc' },
          '&:last-child td': { borderBottom: 'none' },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: { fontSize: '0.8125rem', color: '#64748b', borderTop: '1px solid #f1f5f9' },
        toolbar: { '@media (max-width: 600px)': { paddingLeft: '8px', paddingRight: '8px' } },
        selectLabel: { '@media (max-width: 600px)': { display: 'none' } },
        displayedRows: {
          fontSize: '0.8125rem',
          '@media (max-width: 600px)': { fontSize: '0.75rem' },
        },
        actions: { '@media (max-width: 600px)': { marginLeft: '4px' } },
      },
    },

    // ─── Tabs ─────────────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
        indicator: {
          height: 2.5,
          borderRadius: '2px 2px 0 0',
          backgroundColor: '#14b8a6',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.875rem',
          minWidth: 'auto',
          minHeight: 44,
          padding: '10px 20px',
          color: '#64748b',
          transition: 'all 0.2s ease',
          '&.Mui-selected': { color: '#0f172a' },
        },
      },
    },

    // ─── Chip ─────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'Roboto', sans-serif",
          fontSize: '0.75rem',
          fontWeight: 600,
          borderRadius: 4,
          height: 24,
          border: '1px solid rgba(0,0,0,0.05)',
        },
        label: { padding: '0 8px' },
        sizeSmall: { height: 20, fontSize: '0.6875rem' },
      },
    },

    // ─── Tooltip ─────────────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          color: '#ffffff',
          fontSize: '0.75rem',
          fontWeight: 600,
          padding: '6px 10px',
          borderRadius: 4,
          boxShadow: '0 8px 24px rgba(15,23,42,0.15)',
          backdropFilter: 'blur(4px)',
        },
        arrow: { color: 'rgba(15, 23, 42, 0.95)' },
      },
    },

    // ─── Snackbar / Alert ────────────────────────────────────────────────────
    MuiSnackbar: {
      defaultProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' } },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 8, fontSize: '0.875rem', fontFamily: "'Roboto', sans-serif" },
        message: { fontWeight: 500 },
      },
    },

    // ─── Badge ────────────────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: '0.6875rem', fontWeight: 700, minWidth: 18, height: 18 },
      },
    },

    // ─── Accordion ───────────────────────────────────────────────────────────
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderRadius: '8px !important',
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: 0 },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: { padding: '0 20px', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } },
        content: { margin: '12px 0', '&.Mui-expanded': { margin: '12px 0' } },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: { root: { padding: '0 20px 16px' } },
    },

    // ─── Switch ──────────────────────────────────────────────────────────────
    MuiSwitch: {
      styleOverrides: {
        root: { padding: 7 },
        thumb: { width: 16, height: 16 },
        track: { borderRadius: 999, opacity: 0.25 },
        switchBase: { padding: 9, '&.Mui-checked + .MuiSwitch-track': { opacity: 0.5 } },
      },
    },

    // ─── Skeleton ────────────────────────────────────────────────────────────
    MuiSkeleton: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: 'rgba(226,232,240,0.6)' },
        rectangular: { borderRadius: 8 },
      },
    },
  },
});
